import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Clock, X, Plus, ChevronRight } from 'lucide-react';
import { useCreateTask } from '../hooks/useTasks';

const QUICK_OPTIONS = [
    { label: 'Tomorrow', days: 1 },
    { label: 'In 2 days', days: 2 },
    { label: 'In 3 days', days: 3 },
    { label: 'Next week', days: 7 },
];

function addDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
}

function formatDate(dateStr) {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
    });
}

export default function FollowUpPrompt({ open, onClose, contactId, contactName, accountId, interactionType }) {
    const createTask = useCreateTask();
    const [showCustom, setShowCustom] = useState(false);
    const [customDate, setCustomDate] = useState('');
    const [customTitle, setCustomTitle] = useState('');

    if (!open) return null;

    const defaultTitle = `Follow up with ${contactName || 'contact'}${interactionType ? ` re: ${interactionType}` : ''}`;

    const handleQuickCreate = (days) => {
        const dueDate = addDays(days);
        createTask.mutate({
            title: defaultTitle,
            contact_id: contactId,
            account_id: accountId,
            due_date: dueDate,
            priority: 'medium',
            source: 'follow_up',
        }, {
            onSuccess: () => {
                toast.success(`Follow-up task created for ${formatDate(dueDate)}`);
                onClose();
            },
            onError: () => toast.error('Failed to create task'),
        });
    };

    const handleCustomCreate = () => {
        if (!customDate) { toast.error('Pick a date'); return; }
        createTask.mutate({
            title: customTitle || defaultTitle,
            contact_id: contactId,
            account_id: accountId,
            due_date: customDate,
            priority: 'medium',
            source: 'follow_up',
        }, {
            onSuccess: () => {
                toast.success(`Follow-up task created for ${formatDate(customDate)}`);
                onClose();
            },
            onError: () => toast.error('Failed to create task'),
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-xl shadow-xl border border-stone-200 w-full max-w-sm mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                            <CalendarDays className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Add a follow-up?
                            </p>
                            <p className="text-[11px] text-stone-400">{contactName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-stone-100 text-stone-400">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {!showCustom ? (
                    <div className="p-3 space-y-1.5">
                        <p className="text-xs text-stone-500 mb-2 px-1">{defaultTitle}</p>

                        {QUICK_OPTIONS.map(opt => (
                            <button
                                key={opt.days}
                                onClick={() => handleQuickCreate(opt.days)}
                                disabled={createTask.isPending}
                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-stone-50 border border-stone-100 transition-colors disabled:opacity-50"
                            >
                                <div className="flex items-center gap-2">
                                    <Clock className="w-3.5 h-3.5 text-stone-400" />
                                    <span>{opt.label}</span>
                                </div>
                                <span className="text-xs text-stone-400">{formatDate(addDays(opt.days))}</span>
                            </button>
                        ))}

                        <button
                            onClick={() => setShowCustom(true)}
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-stone-50 border border-stone-100 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Plus className="w-3.5 h-3.5 text-stone-400" />
                                <span>Custom date</span>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
                        </button>

                        <button
                            onClick={onClose}
                            className="w-full text-center py-2 text-xs text-stone-400 hover:text-stone-600"
                        >
                            Skip
                        </button>
                    </div>
                ) : (
                    <div className="p-4 space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Task title</label>
                            <input
                                value={customTitle}
                                onChange={(e) => setCustomTitle(e.target.value)}
                                placeholder={defaultTitle}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Due date</label>
                            <input
                                type="date"
                                value={customDate}
                                onChange={(e) => setCustomDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowCustom(false)}
                                className="flex-1 py-2 text-sm text-stone-600 border border-stone-200 rounded-lg hover:bg-stone-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleCustomCreate}
                                disabled={createTask.isPending || !customDate}
                                className="flex-1 py-2 text-sm font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 disabled:opacity-50"
                            >
                                Create Task
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
